import React, { useState, ReactNode } from 'react';
import { BannerContainer, BannerContainerProps, BannerContent, BannerContentProps, ContentWrapper, HeaderContent, StyledChevron, StyledChevronProps, TitleBadge, TitleBadgeProps, ToggleButton, ToggleButtonProps } from './Banner.styled';

export interface BannerProps {
  // Content props
  titleBadgeText?: string;
  headerComponent?: ReactNode;
  contentComponent?: ReactNode;
  
  // Toggle props
  expandButtonText?: string;
  collapseButtonText?: string;
  initialExpanded?: boolean;
  onToggle?: (isExpanded: boolean) => void;
  
  // New accordion and dismiss props
  isAccordion?: boolean; // If false, always shows expanded without toggle
  isDismissable?: boolean; // If true, shows dismiss button instead of toggle
  dismissText?: string; // Text for dismiss button
  onDismiss?: () => void; // Callback when dismissed
  
  // Styling props
  className?: string;
  
  // Container styling
  containerStyles?: Partial<BannerContainerProps>;
  
  // Badge styling
  badgeStyles?: Partial<TitleBadgeProps>;
  
  // Content styling
  contentStyles?: Partial<BannerContentProps>;
  
  // Toggle button styling
  toggleButtonStyles?: Partial<ToggleButtonProps>;
  
  // Chevron styling
  chevronStyles?: Partial<StyledChevronProps>;
}

const Banner: React.FC<BannerProps> = ({
  titleBadgeText,
  headerComponent,
  contentComponent,
  expandButtonText = 'More',
  collapseButtonText = 'Less',
  initialExpanded = false,
  onToggle,
  isAccordion = true, // Default to accordion behavior
  isDismissable = false, // Default to not dismissable
  dismissText = 'Dismiss',
  onDismiss,
  className,
  containerStyles = {},
  badgeStyles = {},
  contentStyles = {},
  toggleButtonStyles = {},
  chevronStyles = {},
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [isDismissed, setIsDismissed] = useState(false);

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onToggle?.(newExpanded);
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  // Don't render if dismissed
  if (isDismissed) {
    return null;
  }

  // Determine if content should be shown
  const shouldShowContent = !isAccordion || isExpanded;
  
  // Determine if toggle button should be shown
  const shouldShowToggle = contentComponent && isAccordion && !isDismissable;
  
  // Determine if dismiss button should be shown
  const shouldShowDismiss = contentComponent && isDismissable;

  return (
    <BannerContainer 
      className={className}
      {...containerStyles}
    >
      {titleBadgeText && (
        <TitleBadge {...badgeStyles}>
          {titleBadgeText}
        </TitleBadge>
      )}
      
      <ContentWrapper>
        <HeaderContent>
          {headerComponent}
          
          {contentComponent && (
            <BannerContent 
              $isExpanded={shouldShowContent}
              {...contentStyles}
            >
              {contentComponent}
            </BannerContent>
          )}
        </HeaderContent>
        
        {shouldShowToggle && (
          <ToggleButton 
            onClick={handleToggle}
            {...toggleButtonStyles}
          >
            {isExpanded ? collapseButtonText : expandButtonText}
            <StyledChevron
              $isExpanded={isExpanded}
              size={chevronStyles.$iconSize || 20}
              {...chevronStyles}
            />
          </ToggleButton>
        )}
        
        {shouldShowDismiss && (
          <ToggleButton 
            onClick={handleDismiss}
            {...toggleButtonStyles}
          >
            {dismissText}
          </ToggleButton>
        )}
      </ContentWrapper>
    </BannerContainer>
  );
};

export default Banner;